import type {
  Action,
  AgentMessage,
  ElementInfo,
  ResolvedSdkConfig,
  SdkConfig,
  SdkMessage,
  Tier,
} from './types';
import { configureContext, readContext, stopWatchingRoute, watchRoute } from './context';
import { extractElements } from './extractor';
import { captureHtml } from './html';
import { ActionError, configureExecutor, execute } from './executor';
import {
  clear as clearHighlights,
  destroyHighlighter,
  highlightTarget,
  markAll,
} from './highlighter';
import { DEFAULT_REDACT_SELECTORS } from './redact';
import { destroyShell, shellHost } from './shell';
import { configureTelemetry, emit, startTelemetry, stopTelemetry } from './telemetry';
import { Transport, type ConnectionState } from './transport';
import { log, setDebug } from './logger';
import { uuid } from './util';
import { bindUser, flush as flushState, load as loadState, save as saveState } from './persist';
import {
  appendAgentMessage,
  restoreTranscript,
  destroyPanel,
  isOpen,
  mountPanel,
  renderBlocks,
  appendError,
  setConnectionState,
  setWidth,
  setOpen,
  setShellVisible,
  setStatus,
} from './panel';

const DEFAULT_AGENT_PATH = '/ws';

function bundleOrigin(): string {
  const script = ownScript();
  if (script?.src) {
    try {
      return new URL(script.src, window.location.href).origin;
    } catch {
      return window.location.origin;
    }
  }
  return window.location.origin;
}

function resolveConfig(input: SdkConfig): ResolvedSdkConfig {
  if (!input.appId) throw new Error('web-sdk: appId is required');

  const origin = input.loaderOrigin ?? bundleOrigin();
  const wsOrigin = origin.replace(/^http/, 'ws');

  return {
    ...input,
    token: input.token ?? '',
    agentUrl:
      input.agentUrl ?? `${wsOrigin}${DEFAULT_AGENT_PATH}?appId=${encodeURIComponent(input.appId)}`,
    entitySelector: input.entitySelector ?? '[data-entity]',
    autoOpenDelayMs: input.autoOpenDelayMs ?? 5_000,
    contextMode: input.contextMode ?? 'elements',
    maxHtmlBytes: input.maxHtmlBytes ?? 1_500_000,
    appMeta: input.appMeta ?? {},
    mode: input.mode ?? 'push',
    panelWidth: input.panelWidth ?? 400,
    theme: input.theme ?? 'system',
    title: input.title ?? 'Alpha',
    requireApprovalForAllActions: input.requireApprovalForAllActions ?? false,
    redactSelectors: input.redactSelectors ?? [],
    debug: input.debug ?? false,
  };
}

export class WebSdk {
  private readonly config: ResolvedSdkConfig;
  private readonly transport: Transport;
  private readonly sessionId: string;
  private sessionStarted = false;
  private destroyed = false;
  private revealTimer: ReturnType<typeof setTimeout> | undefined;
  private contextUpdateTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly approved = new Map<string, { action: Action; tier: Tier }>();
  private readonly pendingApprovals = new Map<string, { action: Action; tier: Tier }>();

  constructor(input: SdkConfig) {
    this.config = resolveConfig(input);
    setDebug(this.config.debug);

    bindUser(this.config.user?.id ?? '');

    const saved = loadState();
    this.sessionId = saved.sessionId ?? uuid();
    saveState({ sessionId: this.sessionId });

    configureContext({
      entitySelector: this.config.entitySelector,
      appMeta: this.config.appMeta,
    });
    configureExecutor([...DEFAULT_REDACT_SELECTORS, ...this.config.redactSelectors]);

    mountPanel({
      title: this.config.title,
      theme: this.config.theme,
      mode: this.config.mode,
      hostSelector: this.config.hostSelector,
      width: this.config.panelWidth,
      callbacks: {
        onUserMessage: (text) => this.sendUserMessage(text),
        onApproval: (requestId, approved) => this.answerApproval(requestId, approved),
        onOpenChange: (open) => emit('intent_inferred', { panel_open: open }),
      },
    });

    if (saved.blocks?.length) restoreTranscript(saved.blocks, saved.approvals ?? {});
    if (saved.width) setWidth(saved.width);
    if (saved.open) setOpen(true);
    else this.scheduleReveal(saved.dismissed === true);

    this.transport = new Transport(this.config.agentUrl, {
      onMessage: (message) => this.route(message),
      onOpen: () => this.openSession(),
      onStateChange: (value) => this.onConnectionState(value),
    });

    configureTelemetry({
      appId: this.config.appId,
      sessionId: this.sessionId,
      send: (message) => this.transport.send(message),
    });
    startTelemetry(shellHost());

    watchRoute(() => this.sendContextUpdate());
    this.transport.connect();

    window.addEventListener('pagehide', flushState, { capture: true });

    emit('page_view', { title: document.title });
    log.info(`booted session ${this.sessionId} for ${this.config.appId}`);
  }

  private scheduleReveal(dismissed: boolean): void {
    const delay = this.config.autoOpenDelayMs;
    if (delay <= 0) return;

    setShellVisible(false);
    this.revealTimer = setTimeout(() => {
      setShellVisible(true);
      if (dismissed || isOpen()) return;
      setOpen(true, false);
    }, delay);
  }

  private onConnectionState(value: ConnectionState): void {
    setConnectionState(value);
    if (value === 'closed') setStatus('');
  }

  private send(message: SdkMessage): void {
    this.transport.send(message);
  }

  private elements(): ElementInfo[] {
    return extractElements({
      sdkRoot: shellHost(),
      redactSelectors: this.config.redactSelectors,
    });
  }

  private contextPayload(): { elements: ElementInfo[]; html?: string } {
    const elements = this.elements();
    const mode = this.config.contextMode;
    if (mode === 'elements') return { elements };

    const captured = captureHtml({
      redactSelectors: this.config.redactSelectors,
      maxBytes: this.config.maxHtmlBytes,
    });
    return { elements: mode === 'html' ? [] : elements, html: captured.html };
  }

  private openSession(): void {
    const message: SdkMessage = {
      type: 'session_init',
      app_id: this.config.appId,
      token: this.config.token,
      session_id: this.sessionId,
      user: {
        id: this.config.user?.id ?? 'anonymous',
        traits: this.config.user?.traits ?? {},
      },
      context: readContext(),
      ...this.contextPayload(),
    };
    this.send(message);
    this.sessionStarted = true;
  }

  private sendContextUpdate(): void {
    if (!this.sessionStarted) return;
    clearHighlights();
    this.send({ type: 'context_update', context: readContext(), ...this.contextPayload() });
    emit('page_view', { title: document.title });
  }

  private sendUserMessage(text: string): void {
    clearHighlights();
    this.send({ type: 'user_message', text, ...this.contextPayload() });
    emit('user_message', { text });
    setStatus('Thinking…');
  }

  private answerApproval(requestId: string, approved: boolean): void {
    const pending = this.pendingApprovals.get(requestId);
    this.pendingApprovals.delete(requestId);
    if (approved && pending) this.approved.set(requestId, pending);
    else clearHighlights();
    this.send({ request_id: requestId, approved });
    emit('agent_action', {
      stage: 'approval',
      request_id: requestId,
      approved,
      tier: pending?.tier ?? null,
      kind: pending?.action.kind ?? null,
    });
  }

  private guardExecute(requestId: string, action: Action): string | null {
    const record = this.approved.get(requestId);
    if (record) {
      this.approved.delete(requestId);
      return null;
    }
    if (this.config.requireApprovalForAllActions) {
      return `Refused: "${action.kind}" requires explicit approval in this application.`;
    }
    return null;
  }

  private runExecute(requestId: string, action: Action): void {
    const refusal = this.guardExecute(requestId, action);
    if (refusal) {
      log.warn(refusal, action);
      this.send({ request_id: requestId, result: refusal });
      emit('agent_action', { stage: 'refused', kind: action.kind, request_id: requestId });
      return;
    }

    let result: string;
    try {
      result = execute(action);
      if (action.kind !== 'highlight') clearHighlights();
      emit('agent_action', { stage: 'executed', kind: action.kind, request_id: requestId });
    } catch (error) {
      result =
        error instanceof ActionError
          ? `Could not complete: ${error.message}`
          : 'Could not complete that action.';
      log.warn('action failed', error);
      emit('agent_action', { stage: 'failed', kind: action.kind, request_id: requestId });
    }

    this.send({ request_id: requestId, result });
    this.scheduleContextUpdate();
  }

  /**
   * React and friends batch state updates, so the DOM has not changed yet when
   * execute returns. Reading it synchronously sends the agent the pre-render map
   * and it goes on to act on indices that no longer exist. Two frames plus a
   * short settle covers a state update, its re-render and any transition.
   */
  private scheduleContextUpdate(): void {
    clearTimeout(this.contextUpdateTimer);
    this.contextUpdateTimer = setTimeout(() => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (this.destroyed) return;
          this.send({ type: 'context_update', context: readContext(), ...this.contextPayload() });
        }),
      );
    }, 120);
  }

  private route(message: AgentMessage): void {
    log.debug('inbound', message.type);

    switch (message.type) {
      case 'extract':
        this.send({ request_id: message.request_id, ...this.contextPayload() });
        break;

      case 'execute':
        this.runExecute(message.request_id, message.action);
        break;

      case 'highlight':
        highlightTarget(message.target.index);
        break;

      case 'request_approval': {
        if (!message.action) {
          log.warn('declining an approval whose action this SDK cannot perform');
          setStatus('');
          appendError('The assistant proposed something this app cannot do, so it was declined.');
          setShellVisible(true);
          this.send({ request_id: message.request_id, approved: false });
          emit('agent_action', {
            stage: 'unsupported',
            request_id: message.request_id,
            tier: message.tier,
          });
          break;
        }

        this.pendingApprovals.set(message.request_id, {
          action: message.action,
          tier: message.tier,
        });
        setStatus('');
        if (!isOpen()) setOpen(true);
        renderBlocks({
          blocks: [
            {
              kind: 'approval',
              request_id: message.request_id,
              tier: message.tier,
              description: message.description,
            },
          ],
        });
        break;
      }

      case 'panel':
        setStatus('');
        renderBlocks(message.render);
        setShellVisible(true);
        break;

      case 'assistant_message':
        setStatus('');
        appendAgentMessage(message.text);
        setShellVisible(true);
        break;

      case 'status':
        if (message.state === 'error') {
          setStatus('');
          appendError(message.text);
          setShellVisible(true);
        } else {
          setStatus(message.text);
        }
        break;
    }
  }

  open(): void {
    setOpen(true);
  }

  close(): void {
    setOpen(false);
  }

  showMarks(): void {
    markAll(this.elements());
  }

  hideMarks(): void {
    clearHighlights();
  }

  ask(text: string): void {
    setOpen(true);
    this.sendUserMessage(text);
  }

  get sessionIdValue(): string {
    return this.sessionId;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    window.removeEventListener('pagehide', flushState, { capture: true });
    flushState();
    clearTimeout(this.revealTimer);
    clearTimeout(this.contextUpdateTimer);
    stopWatchingRoute();
    stopTelemetry();
    this.transport.close();
    destroyHighlighter();
    destroyPanel();
    destroyShell();
  }
}

let instance: WebSdk | null = null;

export function init(config: SdkConfig): WebSdk {
  if (instance) {
    log.warn('already initialised; returning the existing instance');
    return instance;
  }
  instance = new WebSdk(config);
  return instance;
}

export function current(): WebSdk | null {
  return instance;
}

const OWN_SCRIPT =
  document.currentScript instanceof HTMLScriptElement ? document.currentScript : null;

function ownScript(): HTMLScriptElement | null {
  return OWN_SCRIPT ?? document.querySelector<HTMLScriptElement>('script[src*="websdk"]');
}

function readScriptConfig(script: HTMLScriptElement | null): Partial<SdkConfig> {
  if (!script) return {};
  const data = script.dataset;
  const params = new URL(script.src, window.location.href).searchParams;
  const config: Partial<SdkConfig> = {};

  const appId = data.appId ?? params.get('appId') ?? undefined;
  if (appId) config.appId = appId;
  const token = data.token ?? params.get('token') ?? window.__AGENT_TOKEN__;
  if (token) config.token = token;
  const agentUrl = data.agentUrl ?? params.get('agentUrl') ?? undefined;
  if (agentUrl) config.agentUrl = agentUrl;
  if (data.hostSelector) config.hostSelector = data.hostSelector;
  if (data.entitySelector) config.entitySelector = data.entitySelector;
  if (
    data.contextMode === 'elements' ||
    data.contextMode === 'html' ||
    data.contextMode === 'both'
  ) {
    config.contextMode = data.contextMode;
  }
  if (data.title) config.title = data.title;
  if (data.mode === 'push' || data.mode === 'overlay') config.mode = data.mode;
  if (data.theme === 'light' || data.theme === 'dark' || data.theme === 'system') {
    config.theme = data.theme;
  }
  const width = Number(data.panelWidth);
  if (Number.isFinite(width) && width > 0) config.panelWidth = width;
  if (data.redactSelectors) {
    config.redactSelectors = data.redactSelectors.split(',').map((value) => value.trim());
  }
  if (data.debug !== undefined) config.debug = data.debug !== 'false';

  return config;
}

function readConfig(): SdkConfig | null {
  const merged = { ...readScriptConfig(ownScript()), ...(window.__AGENT_CFG__ ?? {}) };
  return merged.appId ? (merged as SdkConfig) : null;
}

function boot(): void {
  const config = readConfig();
  if (!config) {
    log.warn(
      'no configuration found; add data-app-id to the script tag or set window.__AGENT_CFG__',
    );
    return;
  }
  try {
    init(config);
  } catch (error) {
    log.error('initialisation failed', error);
  }
}

window.AgentSDK = { init, current, version: __SDK_VERSION__ };

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

export type { Action, ElementInfo, SdkConfig, Tier } from './types';
